import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import Head from "next/head";

import parlors from "@/constants/parlors_detail.json";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";
import {
  buildMubinsoPageProps,
  hasMubinsoCost,
} from "@/utils/buildMubinsoPageProps";
import { MubinsoDetailContainer } from "@/containers/MubinsoDetailContainer";

/**
 * 장례식장별 무빈소 장례 안내 페이지
 * - URL: parlor.goifuneral.co.kr/place/{id}/mubinso/
 * - CTA → www.goifuneral.co.kr/match/request/?template=mubinso (개편된 무빈소 퍼널)
 *
 * getStaticPaths: 무빈소 총액이 있는 장례식장만 prerender. 페이지 본문의 핵심이
 * 무빈소 예상 비용이라 값이 없으면 빈 페이지가 색인된다.
 */
export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult =
    skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) return skipBuildResult;

  const paths = parlors.filter(hasMubinsoCost).map((parlor) => ({
    params: { id: parlor.id.toString() },
  }));

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const parlorId = parseInt(params?.id as string);
  const {
    template,
    companyName,
    address,
    sidoName,
    sigunguName,
    latitude,
    longitude,
    hasCoordinates,
    mainImage,
    cost,
    binsoInfo,
    binsoRows,
    feeRows,
    placeInfo,
    compareRows,
    steps,
    faqs,
    nearbyParlors,
    charnels,
    crematoria,
    regionHref,
    serviceHref,
    title,
    description,
    keywords,
    jsonLd,
  } = buildMubinsoPageProps(parlorId);

  return {
    props: {
      parlorId,
      template,
      companyName,
      address,
      sidoName,
      sigunguName,
      latitude: latitude ?? null,
      longitude: longitude ?? null,
      hasCoordinates,
      mainImage,
      cost,
      binsoInfo,
      binsoRows,
      feeRows,
      placeInfo,
      compareRows,
      steps,
      faqs,
      nearbyParlors,
      charnels,
      crematoria,
      regionHref,
      serviceHref,
      pageDescription: description,
      pageKeywords: keywords,
      pagePublishedTime: "2026-08-24T00:00:00+09:00",
      pageOgTitle: title,
      pageOgDescription: description,
      pageOgImage:
        mainImage || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      pageJsonLd: JSON.stringify(jsonLd),
      pageCanonical: `https://parlor.goifuneral.co.kr/place/${parlorId}/mubinso/`,
    },
  };
};

type MubinsoPageProps = Omit<
  Parameters<typeof MubinsoDetailContainer>[0],
  "latitude" | "longitude"
> & {
  latitude: number | null;
  longitude: number | null;
  pageJsonLd?: string;
};

const MubinsoPage = ({ pageJsonLd, ...containerProps }: MubinsoPageProps) => {
  return (
    <>
      {pageJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: pageJsonLd }}
          />
        </Head>
      )}
      <MubinsoDetailContainer
        {...containerProps}
        latitude={containerProps.latitude ?? undefined}
        longitude={containerProps.longitude ?? undefined}
      />
    </>
  );
};

export default MubinsoPage;
